/**
 * Configuration placeholders and environment URLs.
 *
 * CLAUDE.md rule 9: prices, included minutes, and similar knobs live here (and in the D1 `plans`
 * table), never inline in code. Values marked "Alex and Ed" in REQUIREMENTS.md Section 13 are
 * placeholders until the partners set them.
 */

export const COMPANY_NAME = "Magarac Solutions";

/** Product name (decided 2026-09-23). The repo is magaracsolutions/joe360. */
export const PRODUCT_NAME = "J360";

export type PlaceholderOwner = "Alex and Ed" | "Decided";

export interface Placeholder<T> {
  value: T;
  owner: PlaceholderOwner;
}

/** REQUIREMENTS.md Section 13, verbatim. Money in whole dollars here; convert to cents at use. */
export const PLACEHOLDERS = {
  "plan.starter.price": { value: 79, owner: "Alex and Ed" },
  "plan.team.price": { value: 149, owner: "Alex and Ed" },
  "plan.team_receptionist.price": { value: 299, owner: "Alex and Ed" },
  "plan.receptionist_only.price": { value: 99, owner: "Alex and Ed" },
  "seat.price": { value: 30, owner: "Alex and Ed" },
  "receptionist.included_minutes.team_receptionist": { value: 500, owner: "Alex and Ed" },
  "receptionist.included_minutes.receptionist_only": { value: 300, owner: "Alex and Ed" },
  "receptionist.package.minutes": { value: 60, owner: "Decided" },
  "receptionist.package.price": { value: 24, owner: "Decided" },
  "receptionist.package.expiry_days": { value: 365, owner: "Decided" },
  "websites.addon.price": { value: 39, owner: "Alex and Ed" },
  "domain.markup_per_year": { value: 8, owner: "Decided" },
  "connect.application_fee_percent.default": { value: 0.75, owner: "Decided" },
  "dunning.grace_days": { value: 7, owner: "Decided" },
  "reminder.hours_before": { value: 24, owner: "Decided" },
  "hold.ttl_minutes": { value: 5, owner: "Decided" },
} as const satisfies Record<string, Placeholder<number>>;

export type PlaceholderKey = keyof typeof PLACEHOLDERS;

export function placeholder(key: PlaceholderKey): number {
  return PLACEHOLDERS[key].value;
}

/** Slot holds expire after this many minutes (SCH-5). */
export const HOLD_TTL_MINUTES = PLACEHOLDERS["hold.ttl_minutes"].value;

/** Availability engine knobs (Phase 2). Tenant settings override the defaults per tenant. */
export const SCHEDULING = {
  defaultWindowStepMin: 60,
  defaultTravelPadMin: 20,
  maxTravelPadMin: 120,
  /** findSlots refuses ranges longer than this many days. */
  maxRangeDays: 31,
  maxSlots: 2000,
  /** Live Distance Matrix elements a single findSlots call may request before falling back. */
  travelPadLiveBudget: 60,
  /** Idempotency keys inside the tenant object are pruned after this long. */
  idempotencyRetentionHours: 24,
} as const;

export const MAPS = {
  cacheTtlDays: 30,
  /** Coordinates are rounded to this many decimals for cache keys (3 = about 110 m). */
  coordRoundDecimals: 3,
  /** Fake provider speed assumption for local dev and tests. */
  fakeKmPerHour: 40,
} as const;

/** Default Stripe Connect application fee in basis points (BIL-9: 0.75%). */
export const DEFAULT_APPLICATION_FEE_BP = Math.round(
  PLACEHOLDERS["connect.application_fee_percent.default"].value * 100,
);

/** Signed file links (recordings, exports, PDFs) are valid for this long. */
export const SIGNED_URL_TTL_SECONDS = 15 * 60;

/** BIL-10: how long a "download all my data" zip is kept, and how many a business may ask for per day. */
export const DATA_EXPORT_KEEP_DAYS = 7;
export const DATA_EXPORTS_PER_DAY = 3;

/** Impersonation sessions are time-boxed (ACC-7). */
export const IMPERSONATION_MAX_MINUTES = 60;

/**
 * Domains. Production runs on the customer-facing company domain (magaracsolutions.com) with
 * flat hostnames (app., api., admin., book.); the apex stays the marketing site. Staging runs on
 * magaracsolutions.ai with flat `staging-*` hostnames, so staging cookies, test mail, and mistakes
 * never touch the customer domain. Universal SSL covers one level only, which is why every host
 * is flat. TODO(phase-8): customer sites under `sitesRoot` are two levels deep and need Advanced
 * Certificate Manager or a different layout.
 */
export const DOMAINS = {
  root: "magaracsolutions.com",
  sitesRoot: "sites.magaracsolutions.com",
  stagingRoot: "magaracsolutions.ai",
} as const;

export type EnvName = "local" | "test" | "staging" | "production";

export const LOCAL_PORTS = {
  api: 8787,
  jobs: 8788,
  app: 5173,
  admin: 5174,
  booking: 5175,
} as const;

export interface EnvUrls {
  api: string;
  app: string;
  admin: string;
  booking: string;
  sitesRoot: string;
}

/** Public URLs per environment. Local and test point at the Vite and wrangler dev ports. */
export function urlsFor(env: EnvName): EnvUrls {
  switch (env) {
    case "local":
    case "test":
      return {
        api: `http://localhost:${LOCAL_PORTS.api}`,
        app: `http://localhost:${LOCAL_PORTS.app}`,
        admin: `http://localhost:${LOCAL_PORTS.admin}`,
        booking: `http://localhost:${LOCAL_PORTS.booking}`,
        sitesRoot: `localhost:${LOCAL_PORTS.api}`,
      };
    case "staging":
      return {
        api: `https://staging-api.${DOMAINS.stagingRoot}`,
        app: `https://staging-app.${DOMAINS.stagingRoot}`,
        admin: `https://staging-admin.${DOMAINS.stagingRoot}`,
        booking: `https://staging-book.${DOMAINS.stagingRoot}`,
        sitesRoot: `staging-sites.${DOMAINS.stagingRoot}`,
      };
    case "production":
      return {
        api: `https://api.${DOMAINS.root}`,
        app: `https://app.${DOMAINS.root}`,
        admin: `https://admin.${DOMAINS.root}`,
        booking: `https://book.${DOMAINS.root}`,
        sitesRoot: DOMAINS.sitesRoot,
      };
  }
}

/** Magarac Solutions Cloudflare account (admin@magaracsolutions.com). */
export const CLOUDFLARE = {
  accountId: "39e3390b99a644cc6ff1c394598dc753",
} as const;
